import { ReactNode, useCallback } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { useGlobalPrefs, useGlobalPrefsUpdate } from '@/lib/myPrefs';

/**
 * Collapsible section used by the device-overview dashboard to chunk the
 * 22 cards into 7 logical groups (硬件 / 系统 / 网络 / 电池温度 / 存储 /
 * 运行时 / 安全生态).
 *
 * 阶段 A：折叠状态从 localStorage 迁到 B1 prefs.global.ui.dashboard.collapsed
 * (Record<groupId, boolean>)。跟账号走、跨电脑同步。
 *
 * Each group exposes a single "刷新该组" button; the parent keeps the
 * "全部刷新" affordance in the page header. We keep the section header
 * sticky-free (no `position: sticky`) — when several groups are
 * expanded the page is meant to scroll naturally.
 */
export function GroupSection({
  id,
  title,
  subtitle,
  count,
  icon,
  accent,
  defaultOpen = true,
  onRefresh,
  isFetching,
  refreshDisabled,
  children,
}: {
  /** Stable id used as the localStorage key suffix. */
  id: string;
  title: string;
  /** Optional one-line description rendered next to the count badge. */
  subtitle?: string;
  /** Total card count rendered in the header pill. */
  count: number;
  icon?: ReactNode;
  /** Tailwind text-color class for the icon + count pill. */
  accent?: string;
  defaultOpen?: boolean;
  onRefresh?: () => void;
  isFetching?: boolean;
  refreshDisabled?: boolean;
  children: ReactNode;
}) {
  // 由 prefs.global.ui.dashboard.collapsed[id] 决定；未存在的 key 默认 false
  // (展开)，除非父组件传 defaultOpen=false 表示初始折叠。
  const persistedCollapsed = useGlobalPrefs((g) => g.ui.dashboard.collapsed[id]);
  const update = useGlobalPrefsUpdate();
  const collapsed = persistedCollapsed ?? !defaultOpen;

  const toggle = useCallback(() => {
    update({ ui: { dashboard: { collapsed: { [id]: !collapsed } } } });
  }, [id, collapsed, update]);

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between gap-2 border-b border-line/60 pb-1">
        <button
          type="button"
          onClick={toggle}
          className="group flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={!collapsed}
          aria-controls={`group-body-${id}`}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-ink-dim transition group-hover:text-ink" />
          ) : (
            <ChevronDown className="h-4 w-4 text-ink-dim transition group-hover:text-ink" />
          )}
          {icon && <span className={clsx('flex-none', accent)}>{icon}</span>}
          <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
          <span
            className={clsx(
              'rounded-full bg-bg-muted px-1.5 py-0.5 text-[10px] font-medium',
              accent ?? 'text-ink-muted'
            )}
            title={`${count} 张卡片`}
          >
            {count}
          </span>
          {subtitle && (
            <span className="ml-1 truncate text-[11px] text-ink-dim">{subtitle}</span>
          )}
        </button>
        {onRefresh && (
          <button
            className="btn-ghost"
            onClick={onRefresh}
            disabled={isFetching || refreshDisabled}
            title="刷新该组"
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </button>
        )}
      </header>
      {/*
        Layout: CSS Grid with `grid-flow-row-dense` + variable col-span per
        card. Children are wrapped in <div className="col-span-*"> by the
        Dashboard so a card with many fields (build / sensors / storage)
        absorbs its content into 2-3 columns of width instead of becoming
        a single tall tower next to short neighbours. `align-items: stretch`
        (grid default) then equalises heights row-by-row, so within any one
        row the cards look uniformly sized — what the user calls "整个模
        块内不能有长有短".

        `grid-flow-row-dense` lets a 1-col card slot backwards into a gap
        a 3-col card left earlier, which keeps the rhythm tight even when
        card spans alternate. We do NOT use `auto-rows-fr` because rows of
        different content density should stay as tall as their content
        warrants — only intra-row uniformity matters here.
      */}
      <div
        id={`group-body-${id}`}
        className={clsx(
          'gap-3',
          collapsed
            ? 'hidden'
            : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 grid-flow-row-dense'
        )}
      >
        {children}
      </div>
    </section>
  );
}
